import {
  AnnualMultiTrip,
  Europe,
  EuropeHR,
  EuropeLR,
  Worldwide,
  WorldwideHR,
  WorldwideLR,
} from "@/config/app-constant";
import { PRODUCT_DOMAIN } from "@/constants";
import { MarketingChannelDetails } from "@/types/QuestionPayloadType";
import { Attributes, QuoteData, QuotePayload } from "@/types/quote/quote-data";

import isBCUKEnabledByBrand from "./bcuk-enabled.util";
import { TripDetails } from "../common-types/quote/trip-details";

function selectLocationHookFlash(tripDetails: TripDetails | undefined) {
  const toLocation =
    tripDetails?.policyType === AnnualMultiTrip
      ? tripDetails?.area || ""
      : tripDetails?.toLocation || "";

  const locationMapping: Record<
    string,
    { destination: string; destinationAdditions: string }
  > = {
    [WorldwideHR]: { destination: Worldwide, destinationAdditions: "true" },
    [WorldwideLR]: { destination: Worldwide, destinationAdditions: "false" },
    [EuropeHR]: { destination: Europe, destinationAdditions: "true" },
    [EuropeLR]: { destination: Europe, destinationAdditions: "false" },
  };

  return (
    locationMapping[toLocation] || {
      destination: toLocation,
      destinationAdditions: undefined,
    }
  );
}

const initialPayload: QuoteData = {
  type: "IntermediateData",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

export function modifyPayloadSave(
  payLoadResponse: QuotePayload | null | undefined,
  tripDetailsModified?: TripDetails,
  marketingChannelDetails?: MarketingChannelDetails,
  isBCUK?: boolean,
  currentSate?: QuoteData
) {
  const attributes = {
    ...currentSate?.attributes,
    ...payLoadResponse?.attributes,
    tripDetails: tripDetailsModified,
    marketingChannelDetails:
      marketingChannelDetails ||
      payLoadResponse?.attributes?.marketingChannelDetails,
    partnerCode:
      isBCUK && isBCUKEnabledByBrand
        ? "BCUK"
        : payLoadResponse?.attributes?.partnerCode,
  };

  return payLoadResponse?.referenceId
    ? {
        type: currentSate?.type ?? "",
        ...currentSate,
        ...payLoadResponse,
        attributes,
      }
    : {
        ...currentSate,
        ...initialPayload,
        attributes: {
          ...initialPayload.attributes,
          organiser: payLoadResponse?.attributes?.organiser,
          ...attributes,
        } as Attributes,
      };
}

export function modifyQuestionQuotePayload(
  payLoadResponse: QuotePayload | null | undefined,
  currentSate?: QuoteData
) {
  try {
    const travellerDetails = {
      medicalTermsAndConditions:
        payLoadResponse?.attributes?.travellerDetails
          ?.medicalTermsAndConditions,
      traveller: payLoadResponse?.attributes?.travellerDetails?.traveller,
    };

    const modifiedTripDetails = payLoadResponse?.attributes?.tripDetails;
    if (!modifiedTripDetails?.toLocation) {
      delete modifiedTripDetails?.toLocation;
      delete modifiedTripDetails?.toLocationCountryDetails;
    }

    return {
      type: payLoadResponse?.type || "",
      ...currentSate,
      channel: "ONLINE",
      referenceId: payLoadResponse?.referenceId || "",
      version: payLoadResponse?.version || "",
      attributes: {
        ...currentSate?.attributes,
        domain: PRODUCT_DOMAIN,
        organiser: payLoadResponse?.attributes?.organiser,
        tripDetails: modifiedTripDetails,
        travellerDetails,
        marketingChannelDetails:
          payLoadResponse?.attributes?.marketingChannelDetails,
        partnerCode: payLoadResponse?.attributes?.partnerCode,
        experimentDetails: payLoadResponse?.attributes?.experimentDetails,
      },
    };
  } catch {
    return null;
  }
}

export interface PageWindow extends Window {
  pageLoadUrls?: string[];
}

export function accessPagePreviousURL(window: PageWindow | undefined) {
  if (!window) return "";
  window.pageLoadUrls = window.pageLoadUrls ?? [];
  return window.pageLoadUrls.length > 1
    ? window.pageLoadUrls[window.pageLoadUrls.length - 2]
    : "";
}

export function getLastSuccessPage(window: PageWindow | undefined) {
  if (!window) return "";
  window.pageLoadUrls = window.pageLoadUrls ?? [];
  return window.pageLoadUrls.length > 1
    ? window.pageLoadUrls[window.pageLoadUrls.length - 1]
    : "";
}

export function isLastIndexIncludeSameValue(array: string[], value: string) {
  return array.length > 0 && array[array.length - 1] === value;
}

export function storageAvailable() {
  try {
    if (typeof window !== "undefined") {
      const storage = window.sessionStorage;
      const testKey = "__storage_test__";
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return true;
    }
  } catch {
    return false;
  }
}

export const getCookieValue = (name: string, cookie: unknown) =>
  (cookie as string | undefined)
    ?.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`)
    ?.pop() || "";

export default selectLocationHookFlash;

export function capitalizeFirstLetter(name: string) {
  return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : "";
}
